import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Receipt, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const noticeFromRegister = location.state?.notice || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!email || !password) {
      toast.error("يرجى إدخال البريد وكلمة المرور");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.must_change_password) {
        toast.info("يجب تغيير كلمة المرور المؤقتة قبل المتابعة");
        navigate("/change-password");
      } else {
        toast.success("تم تسجيل الدخول بنجاح");
        navigate("/");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "فشل تسجيل الدخول";
      setServerError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#F5F4EF]" dir="rtl">
      {/* Hero side (hidden on small) */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1778222239168-c61866c8e3a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHBhcGVyJTIwdGV4dHVyZSUyMHRlcnJhY290dGElMjBzYW5kfGVufDB8fHx8MTc3ODUyNDA4OHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A33]/90 via-[#1F3A33]/55 to-[#1F3A33]/15" />
        <div className="absolute inset-0 brand-stripes opacity-60" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white text-[#1F3A33] flex items-center justify-center shadow-lg">
              <Receipt className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-readex font-semibold text-2xl leading-none">فواتيري</div>
              <div className="text-xs text-white/70 mt-1 tracking-wider uppercase">Fawateery</div>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-xs mb-6 font-readex">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A3C4B0] soft-pulse" />
              مدعوم بالذكاء الاصطناعي GPT-4o
            </div>
            <h1 className="font-readex font-medium text-4xl xl:text-5xl leading-tight mb-4">
              إدارة فواتيرك ومصاريفك<br />
              <span className="text-[#A3C4B0]">بدقة محاسب محترف</span>
            </h1>
            <p className="text-white/85 text-base leading-relaxed max-w-md">
              تصوير الفواتير، تحليلها تلقائياً، خصم المبالغ من رصيدك، وتصدير تقارير شهرية احترافية.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-[#1F3A33] text-white flex items-center justify-center">
              <Receipt className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <span className="font-readex font-semibold text-xl text-[#1A1C19]">فواتيري</span>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-3 font-readex">مرحباً بعودتك</div>
            <h2 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">تسجيل الدخول</h2>
            <p className="text-[#5C605A] mt-2">أدخل بياناتك لمتابعة إدارة فواتيرك.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {noticeFromRegister && (
              <div
                data-testid="register-success-notice"
                className="bg-[#E8F0EA] border border-[#6A8A76]/40 text-[#1F3A33] rounded-md px-4 py-3.5 font-readex flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-[#1F3A33] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <div className="text-sm leading-relaxed">{noticeFromRegister}</div>
              </div>
            )}
            {serverError && (
              <div
                data-testid="login-error-banner"
                className="bg-[#FBE9E3] border border-[#C84C31]/30 text-[#7A2516] text-sm rounded-md px-4 py-3 font-readex"
              >
                {serverError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">البريد الإلكتروني</label>
              <input
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
                placeholder="name@example.com"
                autoComplete="email"
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">كلمة المرور</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 pe-12 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-[#8A8D86] hover:text-[#1A1C19]"
                  aria-label="إظهار كلمة المرور"
                  data-testid="login-show-password"
                >
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 disabled:cursor-not-allowed transition-colors rounded-md px-6 py-3.5 font-medium font-readex text-base"
            >
              {loading ? "جاري الدخول…" : "دخول"}
            </button>

            <div className="text-center text-sm text-[#5C605A] pt-4">
              ليس لديك حساب؟{" "}
              <Link to="/register" className="text-[#1F3A33] font-medium hover:underline" data-testid="link-register">
                أنشئ حساب جديد
              </Link>
            </div>
            <div className="text-center text-xs text-[#8A8D86] pt-2">
              بالدخول، فأنت توافق على{" "}
              <Link to="/terms" className="text-[#1F3A33] hover:underline" data-testid="link-terms-login">
                اتفاقية الاستخدام وسياسة الخصوصية
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
